import * as Sentry from '@sentry/angular';
import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { BrowserUtils } from '@azure/msal-browser';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// MSAL v5 delivers the popup/iframe auth response back to the opener over a BroadcastChannel.
// Our redirectUri is the app root, so the response lands here; the page must broadcast it and
// NOT boot the SPA, otherwise the opener gives up with redirect_bridge_timeout (~60s) and login
// silently fails. The /redirect-bridge entry point is imported dynamically on purpose — importing
// it eagerly has module side-effects that interfere with normal SPA routing.
function hasMsalAuthResponse(): boolean {
  const fragment = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : '';
  const query = window.location.search.startsWith('?') ? window.location.search.substring(1) : '';
  const params = new URLSearchParams(fragment || query);
  return (params.has('code') || params.has('error')) && params.has('state');
}

function bootstrap(): void {
  if (environment.enableSentry) {
    Sentry.init({
      dsn: environment.sentry.dsn,
      environment: environment.sentry.environment,
      sendDefaultPii: true,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: 1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
      enableLogs: true,
      debug: !environment.production
    });
  }

  if (environment.production) {
    enableProdMode();
  }

  platformBrowserDynamic()
    .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
    .catch((err) => console.log(err));
}

if ((BrowserUtils.isInPopup() || BrowserUtils.isInIframe()) && hasMsalAuthResponse()) {
  import('@azure/msal-browser/redirect-bridge')
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch((err: unknown) => {
      // Surface bridge failures in every environment. Sentry is not initialised on this path
      // (we skip bootstrap), so the console is the only available signal. Log the message only —
      // MSAL error objects can carry correlation IDs / URL fragments we do not want captured.
      console.error(
        'MSAL redirect bridge failed to broadcast the auth response',
        err instanceof Error ? err.message : String(err)
      );
    });
} else {
  bootstrap();
}
